/**
 * Finds the indices of the longest increasing subsequence in the given array of numbers.
 * @param values 
 * @returns 
 */
function longestIncreasingSubsequenceIndices(values: number[]): number[] {
  const n = values.length;
  const predecessors = new Array<number>(n).fill(-1);
  const pileTops: number[] = [];        // indices of values
  const pileIndices: number[] = [];     // stores last index per pile

  for (let i = 0; i < n; i++) {
    const v = values[i];

    // binary search on pile tops
    let lo = 0;
    let hi = pileTops.length;

    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (values[pileTops[mid]] < v) lo = mid + 1;
      else hi = mid;
    }

    if (lo > 0) {
      predecessors[i] = pileTops[lo - 1];
    }

    pileTops[lo] = i;
    pileIndices[lo] = i;
  }

  // reconstruct LIS
  let k = pileTops[pileTops.length - 1];
  const result: number[] = [];

  while (k !== -1) {
    result.push(k);
    k = predecessors[k];
  }

  return result.reverse();
}

/**
 * Finds the indices of stable and unstable elements in the newUIDs array
 * based on the longest increasing subsequence of their order values.
 * @param newUIDs The new order of UIDs.
 * @param nodes A record mapping UIDs to their corresponding nodes with order values.
 * @returns An object containing a set of stable indices and an array of unstable indices.
 */
function findIndicesToUpdate<K extends string | number>(
  newUIDs: K[],
  nodes: Record<K, { order: number }>
): {
  stable: Set<number>;
  unstable: number[];
} {
  const orders = newUIDs.map(uid => nodes[uid].order);
  const lis = longestIncreasingSubsequenceIndices(orders);

  const stable = new Set(lis);
  const unstable: number[] = [];

  for (let i = 0; i < newUIDs.length; i++) {
    if (!stable.has(i)) unstable.push(i);
  }

  return { stable, unstable };
}

/**
 * Computes the minimal set of order updates needed to rearrange nodes
 * according to the newUIDs array, while keeping stable nodes unchanged.
 * @param newUIDs The new order of UIDs.
 * @param nodes A record mapping UIDs to their corresponding nodes with order values.
 * @param stable A set of indices that are stable and should not be changed.
 * @returns A map of UIDs to their new order values.
 */
function computeOrderUpdates<K extends string | number>(
  newUIDs: K[],
  nodes: Record<K, { order: number }>,
  stable: Set<number>
): Map<K, number> {
  const updates = new Map<K, number>();
  let i = 0;
  const n = newUIDs.length;

  while (i < n) {
    if (stable.has(i)) {
      i++;
      continue;
    }

    const start = i;
    while (i < n && !stable.has(i)) i++;
    const end = i - 1;

    const prevIndex = start - 1;
    const nextIndex = i;

    const prevOrder =
      prevIndex >= 0 ? nodes[newUIDs[prevIndex]].order : null;
    const nextOrder =
      nextIndex < n ? nodes[newUIDs[nextIndex]].order : null;

    const count = end - start + 1;

    let base: number;
    let step: number;

    if (prevOrder != null && nextOrder != null) {
      step = (nextOrder - prevOrder) / (count + 1);
      base = prevOrder;
    } else if (prevOrder != null) {
      step = 100;
      base = prevOrder;
    } else if (nextOrder != null) {
      step = -100;
      base = nextOrder;
    } else {
      // entire list moved – rare but possible
      step = 100;
      base = 0;
    }

    for (let k = 1; k <= count; k++) {
      const index = start + k - 1;
      const uid = newUIDs[index];
      updates.set(uid, base + step * k);
    }
  }

  return updates;
}

/**
 * Computes the minimal set of order updates needed to rearrange nodes
 * according to the newUIDs array.
 * @param newUIDs The new order of UIDs.
 * @param nodes A record mapping UIDs to their corresponding nodes with order values.
 * @returns A map of UIDs to their new order values.
 */
export function computeMinimalOrderUpdates<K extends string | number>(
  newUIDs: K[],
  nodes: Record<K, { order: number }>
): Map<K, number> {
  const { stable } = findIndicesToUpdate(newUIDs, nodes);
  return computeOrderUpdates(newUIDs, nodes, stable);
}




/**
 * Calculate a middle order value between two given order values.
 * If one of the values is null or undefined, it assumes an infinite distance.
 * If both are null or undefined, it returns 0.
 * If the distance between the two values is less than 3, it returns a float value.
 * Otherwise, it returns a rounded integer value.
 * @param a The first order value.
 * @param b The second order value.
 * @returns The calculated middle order value.
 */
export function getMiddleOrder(a: number | null | undefined, b: number | null | undefined): number {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return Math.round(b!) - 100;
  }
  if (b == null) {
    return Math.round(a) + 100;
  }
  const middle = (a + b) / 2;
  if (Math.abs(b - a) >= 3) {
    return Math.round(middle);
  }
  return middle;
}

/**
 * Calculate multiple order values evenly spaced between two given order values.
 * If one of the values is null or undefined, it assumes an infinite distance.
 * If both are null or undefined, it generates order values starting from 0.
 * @param a The first order value.
 * @param b The second order value.
 * @param count The number of order values to generate.
 * @returns An array of calculated order values.
 */
export function getOrderValuesBetween(a: number | null, b: number | null | undefined, count: number): number[] {
  const orders: number[] = [];

  if (a == null && b == null) {
    for (let i = 0; i < count; i++) {
      orders.push(i * 100);
    }
    return orders;
  }

  if (a == null) {
    const start = Math.round(b!);
    for (let i = count; i >= 1; i--) {
      orders.push(start + i * 100);
    }
    return orders;
  }

  if (b == null) {
    const start = Math.round(a);
    for (let i = 1; i <= count; i++) {
      orders.push(start - i * 100);
    }
    return orders;
  }

  const step = (b - a) / (count + 1);
  for (let i = 1; i <= count; i++) {
    orders.push(a + step * i);
  }
  return orders;
}

